/**
 * DV-2861: every string the client-visibility control shows. Labels follow
 * the Claude Design prototype verbatim (Tim, 2026-09-16); the dialog titles
 * are sentence case by decision (Steffon, 2026-09-30) — an explicit exception
 * to the Title Case convention for UI chrome.
 */

/** The state a confirm will set. */
export type VisibilityTarget = 'visible' | 'hidden';

/** Named where the copy needs an account and none is known (yet). */
export const FALLBACK_ACCOUNT_NAME = 'The client';

export const VISIBILITY_COPY = {
  menuMakeVisible: 'Make Photoset Visible',
  menuHide: 'Hide Photoset from Client',
  pillHidden: 'Hidden from Client',
  // Source text in Title Case; the banner renders it uppercase in CSS.
  bannerEyebrow: 'Internal Only',
  bannerAction: 'Make Visible to Client',
  bannerActionShort: 'Make Visible',
  toastVisible: 'Page Visible to Client',
  toastHidden: 'Page Hidden from Client',
  saveFailed: 'Could not update client visibility. Please try again.',
  // The customer's side of a hidden set. "Is" capitalized per the Title Case
  // convention (the prototype has "is").
  unavailableTitle: 'This Photo Set Is Unavailable',
  unavailableBody:
    "If you think you're seeing this message in error, please reach out to",
  supportEmail: 'support@planomatic.com'
} as const;

export interface VisibilityDialogCopy {
  title: string;
  body: string;
  /** Hide only. The prototype shows it on both; it reads wrong on Make Visible. */
  reassurance: string | null;
  confirmLabel: string;
}

const accountOrFallback = (accountName: string | null): string =>
  accountName?.trim() || FALLBACK_ACCOUNT_NAME;

export function dialogCopy(
  target: VisibilityTarget,
  accountName: string | null
): VisibilityDialogCopy {
  const account = accountOrFallback(accountName);
  if (target === 'visible') {
    return {
      title: 'Make this page visible to the Client?',
      body: `${account} will be able to view, download, sync, and order services on all assets here. Photos individually marked hidden stay hidden.`,
      reassurance: null,
      confirmLabel: 'Make Visible to Client'
    };
  }
  return {
    title: 'Hide this page from the Client?',
    body: `${account} immediately loses access to every asset on this page. Anything that has previously synced will not be removed from that sync.`,
    reassurance: 'Internal users keep full access, and nothing is deleted.',
    confirmLabel: 'Hide from Client'
  };
}
